import React, { useState, useEffect, useRef } from 'react';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import { askGPT, getAudioFromGPT } from '../components/OpenAIService';
import { db } from '../firebase';
import { getDoc, updateDoc, doc } from 'firebase/firestore';
import { Play, Pause, Volume2, Star, Loader2 } from 'lucide-react';

const Cours = ({ competence, userId }) => {
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);
  const [error, setError] = useState('');

  // MathJax configuration
  const mathJaxConfig = {
    loader: { load: ['[tex]/html'] },
    tex: {
      packages: { '[+]': ['html'] },
      inlineMath: [['$', '$']],
      displayMath: [['$$', '$$']]
    },
    startup: {
      typeset: false
    }
  };

  useEffect(() => {
    const fetchCours = async () => {
      if (!userId) {
        setCours([]);
        setError('Erreur : Vous devez être connecté pour accéder aux cours.');
        setLoading(false);
        return;
      }

      if (!competence?.id) {
        setCours([]);
        setError('Veuillez définir un objectif pour accéder aux cours.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const docRef = doc(db, 'users', userId, 'competences', competence.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const storedCours = docSnap.data().cours;
          if (storedCours) {
            setCours(storedCours.split("\n\n"));
            setTimeout(() => {
              downloadAudio(storedCours);
            }, 0);
          } else {
            await generateNewCours(docRef);
          }
        } else {
          setError('Aucun contenu disponible pour cette compétence.');
        }
      } catch (error) {
        console.error('Erreur lors du chargement du cours :', error);
        setError('Erreur lors du chargement du cours. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchCours();
  }, [userId, competence]);

  const generateNewCours = async (docRef) => {
    if (!competence?.title) {
      setError('Titre de la compétence non défini.');
      return;
    }

    try {
      const prompt = `Explique "${competence.title}" sous forme de texte brut, en 200 mots maximum.
                    Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché.
                    Retourne uniquement le texte brut avec des paragraphes séparés par deux sauts de ligne.`;

      const response = await askGPT(prompt);
      if (!response) {
        throw new Error('Pas de réponse du service GPT');
      }

      const paragraphs = response.split("\n\n");
      setCours(paragraphs);
      await updateDoc(docRef, { cours: response });

      downloadAudio(response);
    } catch (error) {
      console.error('Erreur lors de la génération du cours :', error);
      setError('Erreur lors de la génération du cours. Veuillez réessayer.');
    }
  };

  const convertLatexToReadableText = (text) => {
    if (!text) return '';

    return text
      .replace(/\$\$(.*?)\$\$/gs, (_, formula) => {
        return formula
          .replace(/f'/g, 'f prime')
          .replace(/\\lim_{(.*?)}\\to{(.*?)}/g, 'la limite quand $1 tend vers $2')
          .replace(/\\frac\{(.*?)\}\{(.*?)\}/g, 'fraction de $1 sur $2')
          .replace(/\^/g, ' puissance ')
          .replace(/f\((.*?)\)/g, 'f de $1')
          .replace(/\\cdot/g, ' multiplié par ')
          .replace(/\\sqrt\{(.*?)\}/g, 'racine carrée de $1')
          .replace(/\\left/g, '')
          .replace(/\\right/g, '')
          .replace(/\{(.*?)\}/g, '$1');
      })
      .replace(/\$(.*?)\$/g, (_, formula) => {
        return formula
          .replace(/f'/g, 'f prime')
          .replace(/\\lim_{(.*?)}\\to{(.*?)}/g, 'la limite quand $1 tend vers $2')
          .replace(/\\frac\{(.*?)\}\{(.*?)\}/g, 'fraction de $1 sur $2')
          .replace(/\^/g, ' puissance ')
          .replace(/f\((.*?)\)/g, 'f de $1')
          .replace(/\\cdot/g, ' multiplié par ')
          .replace(/\\sqrt\{(.*?)\}/g, 'racine carrée de $1')
          .replace(/\\left/g, '')
          .replace(/\\right/g, '')
          .replace(/\{(.*?)\}/g, '$1');
      });
  };

  const downloadAudio = async (text) => {
    if (!text) {
      setError('Pas de texte à convertir en audio');
      return;
    }

    setAudioLoading(true);
    try {
      const readableText = convertLatexToReadableText(text);
      const audio = await getAudioFromGPT(readableText);

      if (audio) {
        setAudioUrl(audio);
        setError('');
      } else {
        throw new Error('Impossible de récupérer l\'audio');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'audio :', error);
      setError('Erreur lors du téléchargement de l\'audio. Veuillez réessayer.');
    } finally {
      setAudioLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(error => {
        console.error('Erreur lors de la lecture audio :', error);
        setError('Erreur lors de la lecture audio');
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleAudioTimeUpdate = () => {
    if (!audioRef.current) return;
    const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setAudioProgress(progress);
  };

  const handleSliderChange = (event) => {
    if (!audioRef.current) return;
    const newValue = parseFloat(event.target.value);
    const newTime = (audioRef.current.duration / 100) * newValue;
    audioRef.current.currentTime = newTime;
    setAudioProgress(newValue);
  };

  const renderWithMathJax = (content) => {
    if (!content) return null;

    return (
      <MathJax className="math-content">
        {content}
      </MathJax>
    );
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-4xl mx-auto w-full p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              Présentation des concepts clés
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="mt-4 text-gray-600">Chargement du cours...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {cours.length > 0 ? (
              <>
                {cours.map((paragraph, index) => (
                  <div 
                    key={`math-${index}`}
                    className={`rounded-xl shadow-sm border border-gray-200 p-6
                      ${index % 2 === 0 
                        ? 'bg-gradient-to-br from-blue-50 to-indigo-50' 
                        : 'bg-white'
                      }`}
                  >
                    <div className="prose prose-blue max-w-none">
                      {renderWithMathJax(paragraph)}
                    </div>
                  </div>
                ))}

                <div className="mt-6 space-y-4">
                  <button
                    onClick={handlePlayPause}
                    disabled={audioLoading || !audioUrl}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                      ${audioLoading || !audioUrl
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isPlaying
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    {audioLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Téléchargement...</span>
                      </>
                    ) : isPlaying ? (
                      <>
                        <Pause className="h-5 w-5" />
                        <span>Pause</span>
                      </>
                    ) : (
                      <>
                        <Play className="h-5 w-5" />
                        <span>Écouter le cours</span>
                      </>
                    )}
                  </button>

                  {audioUrl && (
                    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <Volume2 className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-gray-700">Lecture audio</span>
                      </div>
                      <audio
                        ref={audioRef}
                        src={audioUrl}
                        onTimeUpdate={handleAudioTimeUpdate}
                        onEnded={() => setIsPlaying(false)}
                        className="hidden"
                      />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={audioProgress}
                        onChange={handleSliderChange}
                        className="w-full accent-blue-500"
                      />
                    </div>
                  )}
                </div>
              </>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                Aucun contenu disponible.
              </div>
            )}
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

export default Cours;